// JobsProvider.jsx — Phase 4 Step B
//
// An app-wide registry of background/async operations. `useJobs()` exposes
// start/update/end/clear; <JobsMenu/> is the TopBar entry point — a button with a
// running-count badge that opens a dropdown tray listing jobs with progress,
// elapsed time, errors, and a cancel action for cancelable running jobs.
//
// The genuine async job here is plan computation (polled). Foreground operations
// (layout run, PDF export) register as short-lived jobs too, so the tray reflects
// real recent activity rather than sitting empty.
//
// Saved jobs (decided 2 Nov with #85) have their own section, below the
// background jobs: the runs someone chose to keep, under a name — an admin's
// list shows every one in their organisation, with its owner. They come from
// the server, so "Clear finished" never touches them. An unsaved run leaves
// the tray once a newer one finishes (App calls `dropJobs`).

import React from 'react';
import { useTheme } from './theme';

const JobsCtx = React.createContext(null);

export function useJobs() {
  return React.useContext(JobsCtx) || {
    jobs: [], runningCount: 0,
    startJob: () => 0, updateJob: () => {}, endJob: () => {}, clearFinished: () => {},
    registerJob: () => 0, markSeen: () => {}, dropJobs: () => {},
  };
}

let _seq = 0;

export function JobsProvider({ children }) {
  const [jobs, setJobs] = React.useState([]);

  const startJob = React.useCallback((opts) => {
    const id = ++_seq;
    const job = {
      id, kind: 'task', label: 'Working', status: 'running',
      fraction: null, message: null, error: null,
      startedAt: Date.now(), endedAt: null, onCancel: null,
      // §3 badging: a new job raises a dot until the user opens the tray and
      // clicks it. `unattached` marks a job picked up from a previous session
      // that the user has not yet reattached to.
      unseen: true, unattached: false, onOpen: null,
      ...(opts || {}),
    };
    setJobs((list) => [job, ...list].slice(0, 30));
    return id;
  }, []);

  const updateJob = React.useCallback((id, patch) => {
    setJobs((list) => list.map((j) => (j.id === id ? { ...j, ...(patch || {}) } : j)));
  }, []);

  const endJob = React.useCallback((id, patch) => {
    setJobs((list) => list.map((j) => (
      j.id === id ? { ...j, status: 'succeeded', endedAt: Date.now(), fraction: 1, onCancel: null, unseen: true, ...(patch || {}) } : j
    )));
  }, []);

  const clearFinished = React.useCallback(() => {
    setJobs((list) => list.filter((j) => j.status === 'running'));
  }, []);

  /**
   * Add a job the user did NOT start in this tab — one restored from workspace
   * hydration (§2A unattached-running, or §2B history). Keyed on `jobId` so a
   * re-hydration cannot duplicate a row.
   */
  const registerJob = React.useCallback((opts) => {
    const jobId = opts?.jobId;
    let existingId = null;
    setJobs((list) => {
      const hit = jobId ? list.find((j) => j.jobId === jobId) : null;
      if (hit) { existingId = hit.id; return list; }
      const id = ++_seq;
      existingId = id;
      return [{
        id, kind: 'layout', label: 'Job', status: 'running',
        fraction: null, message: null, error: null,
        startedAt: Date.now(), endedAt: null, onCancel: null,
        unseen: true, unattached: false, onOpen: null,
        ...(opts || {}),
      }, ...list].slice(0, 30);
    });
    return existingId;
  }, []);

  /**
   * Take rows out of the tray — an unsaved run, once a newer one finishes.
   * `which` is a list of row ids, or a function of the CURRENT rows returning
   * them (callers in a long-running async loop hold a stale copy of `jobs`).
   */
  const dropJobs = React.useCallback((which) => {
    setJobs((list) => {
      const gone = new Set((typeof which === 'function' ? which(list) : which) || []);
      return gone.size ? list.filter((j) => !gone.has(j.id)) : list;
    });
  }, []);

  /** Clear a row's notification dot — the user has now inspected it (§3). */
  const markSeen = React.useCallback((id) => {
    setJobs((list) => list.map((j) => (j.id === id ? { ...j, unseen: false } : j)));
  }, []);

  const runningCount = jobs.reduce((n, j) => n + (j.status === 'running' ? 1 : 0), 0);
  const unseenCount = jobs.reduce((n, j) => n + (j.unseen ? 1 : 0), 0);

  const value = React.useMemo(
    () => ({ jobs, runningCount, unseenCount, startJob, updateJob, endJob, clearFinished, registerJob, markSeen, dropJobs }),
    [jobs, runningCount, unseenCount, startJob, updateJob, endJob, clearFinished, registerJob, markSeen, dropJobs]
  );
  return <JobsCtx.Provider value={value}>{children}</JobsCtx.Provider>;
}

// ─── UI ──────────────────────────────────────────────────────────────────────

const STATUS = {
  running: (P) => P.accent,
  succeeded: (P) => P.ok,
  failed: (P) => P.danger,
  cancelled: (P) => P.dim,
  // Contract v2.0 §6.2: the backend restarted mid-job. Terminal, but not the
  // user's fault — warn-toned rather than danger-toned.
  interrupted: (P) => P.warn,
};

function elapsedText(job) {
  const end = job.endedAt || Date.now();
  const s = Math.max(0, (end - job.startedAt) / 1000);
  return s < 60 ? `${s.toFixed(s < 10 ? 1 : 0)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function savedWhen(iso) {
  const t = Date.parse(iso || '');
  // Short, so the owner's name keeps its room beside Rename / Remove.
  return Number.isFinite(t)
    ? new Date(t).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;
}

/**
 * @param {Array} saved  rows from savedJobs.savedJobRows (newest first)
 * @param {(row) => void} onOpenSaved / onRenameSaved / onRemoveSaved
 */
export function JobsMenu({ saved = [], onOpenSaved, onRenameSaved, onRemoveSaved } = {}) {
  const { P, FN, MO } = useTheme();
  const { jobs, runningCount, clearFinished, markSeen } = useJobs();
  const [open, setOpen] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(null);

  // Tick while jobs are running so elapsed time stays live.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!runningCount) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [runningCount]);

  const finishedCount = jobs.length - runningCount;
  const hasUnseen = jobs.some((j) => j.unseen);

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} title="Background jobs" style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: MO, fontSize: 10.5, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
        background: open ? P.elev : 'transparent', color: runningCount ? P.ink : P.dim,
        border: `0.5px solid ${open ? P.rule : 'transparent'}`,
      }}>
        Jobs
        {runningCount > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8, background: P.accent, color: P.sheet, fontSize: 9, fontWeight: 700,
          }}>{runningCount}</span>
        )}
        {/* §3: an unread dot for a job started or finished in the background.
            Shown alongside the running count, and on its own when nothing is
            running but something finished while the tray was closed. */}
        {hasUnseen && runningCount === 0 && (
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: P.accent, flexShrink: 0 }} />
        )}
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100,
            width: 340, maxWidth: 'calc(100vw - 32px)', maxHeight: '70vh', overflowY: 'auto',
            background: P.sheet, border: `0.5px solid ${P.rule}`, borderRadius: 12,
            boxShadow: '0 20px 50px rgba(20,16,12,.24)', animation: 'mono-in .16s ease both',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `0.5px solid ${P.rule}` }}>
              <span style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1, textTransform: 'uppercase', color: P.dim }}>Background jobs</span>
              {finishedCount > 0 && (
                <button onClick={(e) => { e.stopPropagation(); clearFinished(); }} style={{
                  fontFamily: MO, fontSize: 10, color: P.muted, background: 'transparent',
                  border: `0.5px solid ${P.rule}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
                }}>Clear finished</button>
              )}
            </div>

            {jobs.length === 0 ? (
              <div style={{ padding: '22px 14px', textAlign: 'center', fontFamily: FN, fontSize: 12, color: P.dim }}>
                No jobs yet. Running a layout, exporting a PDF, or computing plans will show up here.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {jobs.map((j) => {
                  const color = (STATUS[j.status] || STATUS.cancelled)(P);
                  const pct = typeof j.fraction === 'number' ? Math.round(Math.max(0, Math.min(1, j.fraction)) * 100) : null;
                  const clickable = typeof j.onOpen === 'function';
                  const open_ = () => {
                    if (!clickable) return;
                    markSeen(j.id);          // §3: inspecting clears this row's dot
                    j.onOpen(j);
                    setOpen(false);          // hand the workspace back to the user
                  };
                  return (
                    <div key={j.id}
                      onClick={open_}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open_(); } } : undefined}
                      title={clickable ? (j.unattached ? 'Reattach to this job' : 'Load this job into the workspace') : undefined}
                      style={{
                        padding: '11px 14px', borderBottom: `0.5px solid ${P.ruleSoft}`,
                        cursor: clickable ? 'pointer' : 'default',
                        // §3: a running job picked up from a previous session is
                        // highlighted until the user reattaches to it.
                        background: j.unattached ? P.accentSoft : 'transparent',
                        borderLeft: j.unattached ? `2px solid ${P.accent}` : '2px solid transparent',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
                          animation: j.status === 'running' ? 'mono-pulse 1.2s ease-in-out infinite' : 'none',
                        }} />
                        <span style={{ fontFamily: FN, fontSize: 12.5, fontWeight: 500, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.label}</span>
                        {j.unseen && (
                          <span title="Not yet viewed" style={{ width: 6, height: 6, borderRadius: '50%', background: P.accent, flexShrink: 0 }} />
                        )}
                        <span style={{ marginLeft: 'auto', fontFamily: MO, fontSize: 9.5, color: P.dim }}>{elapsedText(j)}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, paddingLeft: 16 }}>
                        <span style={{ fontFamily: MO, fontSize: 9, letterSpacing: 0.4, textTransform: 'uppercase', color }}>
                          {j.status}
                        </span>
                        {j.kind && <span style={{ fontFamily: MO, fontSize: 9, color: P.dim }}>· {j.kind}</span>}
                        {j.status === 'running' && j.onCancel && (
                          <button onClick={(e) => { e.stopPropagation(); j.onCancel(); }} style={{
                            marginLeft: 'auto', fontFamily: MO, fontSize: 9.5, color: P.danger, background: 'transparent',
                            border: `0.5px solid ${P.dangerLine}`, borderRadius: 4, padding: '1px 7px', cursor: 'pointer',
                          }}>Cancel</button>
                        )}
                      </div>

                      {pct != null && j.status === 'running' && (
                        <div style={{ height: 4, borderRadius: 2, background: P.elev, overflow: 'hidden', marginTop: 7, marginLeft: 16 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width .3s ease' }} />
                        </div>
                      )}
                      {(j.message || j.error) && (
                        <div style={{ fontFamily: FN, fontSize: 10.5, color: j.error ? P.danger : P.muted, marginTop: 6, marginLeft: 16, lineHeight: 1.4 }}>
                          {j.error || j.message}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ padding: '12px 14px 8px', borderTop: `0.5px solid ${P.rule}` }}>
              <span style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1, textTransform: 'uppercase', color: P.dim }}>Saved jobs</span>
            </div>
            {saved.length === 0 ? (
              <div style={{ padding: '2px 14px 16px', fontFamily: FN, fontSize: 11.5, color: P.dim, lineHeight: 1.45 }}>
                None yet. “Save job”, next to Download PDF, keeps a layout here under a name.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 4 }}>
                {saved.map((r) => {
                  const openIt = () => { if (onOpenSaved) { onOpenSaved(r); setOpen(false); } };
                  const small = (color, line) => ({
                    fontFamily: MO, fontSize: 9.5, color, background: 'transparent',
                    border: `0.5px solid ${line}`, borderRadius: 4, padding: '1px 7px', cursor: 'pointer',
                  });
                  return (
                    <div key={r.id} role="button" tabIndex={0} onClick={openIt}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIt(); } }}
                      title="Open this saved job in the Auto Imposer"
                      style={{ padding: '9px 14px', borderBottom: `0.5px solid ${P.ruleSoft}`, cursor: 'pointer' }}>
                      <div style={{ fontFamily: FN, fontSize: 12.5, fontWeight: 500, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <span style={{ fontFamily: MO, fontSize: 9.5, color: r.owner ? P.accent : P.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[r.owner ? `by ${r.owner}` : null, savedWhen(r.createdAt)].filter(Boolean).join(' · ')}
                        </span>
                        {confirmRemove === r.id ? (
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => { setConfirmRemove(null); if (onRemoveSaved) onRemoveSaved(r); }}
                              style={small(P.danger, P.dangerLine)}>Remove</button>
                            <button onClick={() => setConfirmRemove(null)} style={small(P.muted, P.rule)}>Keep</button>
                          </span>
                        ) : (
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                            {onRenameSaved && (
                              <button onClick={() => { onRenameSaved(r); setOpen(false); }} style={small(P.muted, P.rule)}>Rename</button>
                            )}
                            {onRemoveSaved && (
                              <button onClick={() => setConfirmRemove(r.id)} title="Forget this saved job. The cartons stay in the library."
                                style={small(P.muted, P.rule)}>Remove</button>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
